import { useState, useEffect } from 'react'
import { supabase } from './supabase'

const SOURCES = [
  { key: 'Pinch of Yum', label: 'Pinch of Yum', emoji: '🌿' },
  { key: 'NYT Cooking', label: 'NYT Cooking', emoji: '📰' },
]

const DEFAULT_CATEGORIES = ['dinner', 'lunch', 'breakfast', 'misc']

export default function Explore({ user, onBack, onSaved, customTabs = [] }) {
  const [activeSource, setActiveSource] = useState('Pinch of Yum')
  const [bundles, setBundles] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [items, setItems] = useState([])
  const [loadingItems, setLoadingItems] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedIds, setSavedIds] = useState(new Set())

  useEffect(() => {
    fetchBundles()
  }, [])

  async function fetchBundles() {
    const { data } = await supabase
      .from('curated_bundles')
      .select('*')
      .order('name')
    setBundles(data || [])
    setLoading(false)
  }

  async function openDetail(bundle) {
    setSelected(bundle)
    setSelectedCategory(bundle.category || 'dinner')
    setLoadingItems(true)
    const { data } = await supabase
      .from('curated_bundle_items')
      .select('*')
      .eq('bundle_id', bundle.id)
      .order('name')
    setItems(data || [])
    setLoadingItems(false)
  }

  function closeDetail() {
    setSelected(null)
    setSelectedCategory(null)
    setItems([])
  }

  async function saveBundle() {
    if (!selected || saving) return
    setSaving(true)

    const { data: membership } = await supabase
      .from('household_members')
      .select('household_id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!membership) { setSaving(false); return }

    const { data: newBundle } = await supabase
      .from('bundles')
      .insert({
        name: selected.name,
        emoji: selected.emoji,
        category: selectedCategory || selected.category,
        bg: selected.bg || '#F4FBF8',
        items: items.length,
        price: Math.round(selected.estimated_price || 0),
        household_id: membership.household_id,
        created_by: user.id,
        is_popular: false,
      })
      .select()
      .single()

    if (newBundle) {
      if (items.length > 0) {
        await supabase.from('bundle_items').insert(
          items.map(item => ({
            bundle_id: newBundle.id,
            name: item.name,
            emoji: item.emoji || '🛒',
            price: item.estimated_price || 0,
            quantity: 1,
          }))
        )
      }
      setSavedIds(prev => new Set([...prev, selected.id]))
      onSaved && onSaved(newBundle)
    }

    setSaving(false)
  }

  const visibleBundles = bundles.filter(b => b.source === activeSource)

  return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Explore</div>
        <div style={{ width: 50 }} />
      </div>

      <div style={styles.sourceRow}>
        {SOURCES.map(s => (
          <button
            key={s.key}
            style={{ ...styles.sourceTab, ...(activeSource === s.key ? styles.sourceTabActive : {}) }}
            onClick={() => setActiveSource(s.key)}
          >
            {s.emoji} {s.label}
          </button>
        ))}
      </div>

      <div style={styles.sourceDesc}>
        {activeSource === 'Pinch of Yum' && (
          <div style={styles.descText}>Real-life recipes that actually get made. Easy weeknight dinners from Lindsay Ostrom's kitchen.</div>
        )}
        {activeSource === 'NYT Cooking' && (
          <div style={styles.descText}>Trusted recipes from New York Times Cooking — weeknight staples to weekend projects.</div>
        )}
      </div>

      <div style={styles.list}>
        {loading ? (
          <div style={styles.empty}>Loading…</div>
        ) : visibleBundles.length === 0 ? (
          <div style={styles.empty}>No bundles found.</div>
        ) : (
          visibleBundles.map(bundle => (
            <div key={bundle.id} style={styles.card} onClick={() => openDetail(bundle)}>
              <div style={{ ...styles.cardEmoji, background: bundle.bg || '#F4FBF8' }}>
                {bundle.emoji}
              </div>
              <div style={styles.cardInfo}>
                <div style={styles.cardName}>{bundle.name}</div>
                <div style={styles.cardDesc}>{bundle.description}</div>
                <div style={styles.cardMeta}>
                  ~${bundle.estimated_price} · {bundle.category}
                </div>
              </div>
              <div style={styles.cardRight}>
                {savedIds.has(bundle.id) ? (
                  <span style={styles.savedBadge}>Saved</span>
                ) : (
                  <span style={styles.arrowIcon}>›</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {selected && (
        <>
          <div style={styles.backdrop} onClick={closeDetail} />
          <div style={styles.sheet}>
            <div style={styles.handle} />

            <div style={styles.sheetHeader}>
              <div style={{ ...styles.sheetEmoji, background: selected.bg || '#F4FBF8' }}>
                {selected.emoji}
              </div>
              <div style={styles.sheetTitleWrap}>
                <div style={styles.sheetTitle}>{selected.name}</div>
                <div style={styles.sheetSource}>{selected.source}</div>
              </div>
            </div>

            {selected.description && (
              <div style={styles.sheetDesc}>{selected.description}</div>
            )}

            <div style={styles.ingredientLabel}>What you'll need</div>

            <div style={styles.ingredientList}>
              {loadingItems ? (
                <div style={styles.loadingItems}>Loading…</div>
              ) : (
                items.map((item, i) => (
                  <div key={i} style={styles.ingredientRow}>
                    <span style={styles.ingredientEmoji}>{item.emoji || '🛒'}</span>
                    <span style={styles.ingredientName}>{item.name}</span>
                    {item.estimated_price > 0 && (
                      <span style={styles.ingredientPrice}>~${item.estimated_price}</span>
                    )}
                  </div>
                ))
              )}
            </div>

            <div style={styles.categorySection}>
              <div style={styles.ingredientLabel}>Save to tab</div>
              <div style={styles.categoryRow}>
                {[...DEFAULT_CATEGORIES, ...customTabs].map(cat => (
                  <button
                    key={cat}
                    style={{
                      ...styles.catBtn,
                      ...(selectedCategory === cat ? styles.catBtnActive : {}),
                    }}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.sheetFooter}>
              <div style={styles.sheetPriceNote}>~${selected.estimated_price} estimated total</div>
              <button
                style={{
                  ...styles.saveBtn,
                  ...(savedIds.has(selected.id) ? styles.saveBtnDone : {}),
                }}
                onClick={saveBundle}
                disabled={saving || savedIds.has(selected.id)}
              >
                {savedIds.has(selected.id) ? '✓ Saved to my bundles' : saving ? 'Saving…' : 'Save to my bundles'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '430px', margin: '0 auto', minHeight: '100vh',
    background: 'white', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
  },
  navBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px 8px', flexShrink: 0,
  },
  backBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  navTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '18px', color: '#1a1a18' },
  sourceRow: {
    display: 'flex', gap: '8px', padding: '4px 20px 12px', flexShrink: 0,
  },
  sourceTab: {
    padding: '8px 16px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: '#f9f9f7', fontSize: '13px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
  },
  sourceTabActive: {
    background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontWeight: '500',
  },
  sourceDesc: {
    padding: '0 20px 10px', flexShrink: 0,
  },
  descText: {
    fontSize: '12px', color: '#aaa', lineHeight: '1.6',
  },
  list: {
    flex: 1, overflowY: 'auto', padding: '0 20px 16px',
  },
  empty: { fontSize: '13px', color: '#aaa', padding: '40px 0', textAlign: 'center' },
  card: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '11px 13px', border: '0.5px solid #eee',
    borderRadius: '14px', marginBottom: '8px', cursor: 'pointer',
    background: 'white',
  },
  cardEmoji: {
    width: '48px', height: '48px', borderRadius: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '24px', flexShrink: 0,
  },
  cardInfo: { flex: 1, minWidth: 0 },
  cardName: { fontSize: '14px', fontWeight: '500', color: '#1a1a18', marginBottom: '2px' },
  cardDesc: {
    fontSize: '11px', color: '#888', marginBottom: '3px',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardMeta: { fontSize: '11px', color: '#aaa' },
  cardRight: { flexShrink: 0 },
  arrowIcon: { fontSize: '20px', color: '#ccc' },
  savedBadge: {
    fontSize: '10px', fontWeight: '500', color: '#0F6E56',
    background: '#E1F5EE', padding: '3px 8px', borderRadius: '20px',
  },
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100,
  },
  sheet: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: '430px', background: 'white',
    borderRadius: '20px 20px 0 0', padding: '12px 20px 32px',
    zIndex: 101, display: 'flex', flexDirection: 'column', gap: '12px',
    maxHeight: '85vh', fontFamily: "'DM Sans', sans-serif",
    overflowY: 'auto',
  },
  handle: {
    width: '36px', height: '4px', borderRadius: '2px',
    background: '#e0e0e0', margin: '0 auto 4px', flexShrink: 0,
  },
  sheetHeader: {
    display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0,
  },
  sheetEmoji: {
    width: '52px', height: '52px', borderRadius: '13px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '26px', flexShrink: 0,
  },
  sheetTitleWrap: { flex: 1 },
  sheetTitle: { fontSize: '17px', fontWeight: '600', color: '#1a1a18' },
  sheetSource: { fontSize: '12px', color: '#1D9E75', marginTop: '2px' },
  sheetDesc: {
    fontSize: '13px', color: '#555', lineHeight: '1.6',
    padding: '4px 0', flexShrink: 0,
  },
  ingredientLabel: {
    fontSize: '11px', fontWeight: '600', color: '#aaa',
    textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0,
  },
  ingredientList: { flex: 1 },
  loadingItems: { fontSize: '13px', color: '#aaa', padding: '12px 0' },
  ingredientRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 0', borderBottom: '0.5px solid #f5f5f5',
  },
  ingredientEmoji: { fontSize: '16px', width: '22px', textAlign: 'center', flexShrink: 0 },
  ingredientName: { flex: 1, fontSize: '13px', color: '#1a1a18' },
  ingredientPrice: { fontSize: '12px', color: '#aaa', flexShrink: 0 },
  categorySection: { flexShrink: 0 },
  categoryRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  catBtn: {
    padding: '6px 14px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: '#f9f9f7', fontSize: '12px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  catBtnActive: {
    background: '#1D9E75', borderColor: '#1D9E75', color: 'white', fontWeight: '500',
  },
  sheetFooter: { flexShrink: 0, paddingTop: '4px' },
  sheetPriceNote: {
    fontSize: '11px', color: '#aaa', textAlign: 'center', marginBottom: '10px',
  },
  saveBtn: {
    width: '100%', padding: '13px', background: '#1D9E75',
    color: 'white', border: 'none', borderRadius: '12px',
    fontSize: '14px', fontWeight: '500', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  saveBtnDone: {
    background: '#E1F5EE', color: '#0F6E56', cursor: 'default',
  },
}
